import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Columns3, Wand2 } from "lucide-react";
import { api, errorMessage } from "@/lib/api";
import { STANDARD_FIELDS, resolveFieldMap } from "@/lib/featureInfo";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

// Per-layer "map your columns" dialog: standard parcel field → source column in the uploaded file.
export function FieldMapDialog({ layer, onClose, onSaved }) {
  const [columns, setColumns] = useState([]);
  const [map, setMap] = useState(layer.field_map || {});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get(`/map/layers/${layer.id}/geojson`).then(({ data }) => {
      const cols = new Set();
      (data.features || []).slice(0, 200).forEach((f) => Object.keys(f.properties || {}).forEach((k) => cols.add(k)));
      setColumns([...cols]);
      if (!layer.field_map) {
        const sample = {}; [...cols].forEach((c) => { sample[c] = 1; });
        setMap(resolveFieldMap({}, sample));
      }
    }).catch((err) => toast.error(errorMessage(err)));
  }, [layer.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const autoDetect = () => { const sample = {}; columns.forEach((c) => { sample[c] = 1; }); setMap(resolveFieldMap({}, sample)); };
  const save = async () => {
    setSaving(true);
    try { const { data } = await api.patch(`/admin/layers/${layer.id}`, { field_map: map }); onSaved(data); toast.success("Column mapping saved"); onClose(); }
    catch (err) { toast.error(errorMessage(err)); } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="field-map-dialog" className="max-w-md rounded-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Columns3 size={16} /> Map columns · {layer.name}</DialogTitle>
          <DialogDescription>Choose which column of this file feeds each standard field on the record card. Unmapped columns are still listed below the standard ones.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          {STANDARD_FIELDS.map(([std, label]) => (
            <div key={std} className="grid grid-cols-[120px_1fr] items-center gap-3 text-sm">
              <label htmlFor={`fm-${std}`} className="font-medium">{label}</label>
              <select id={`fm-${std}`} data-testid={`field-map-select-${std}`} value={map[std] ?? ""} onChange={(e) => setMap({ ...map, [std]: e.target.value })}
                className="h-9 rounded-lg border bg-background px-2 text-sm">
                <option value="">— none —</option>
                {columns.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          ))}
          {columns.length === 0 && <p className="text-xs text-muted-foreground">Loading columns…</p>}
        </div>
        <div className="flex items-center justify-between pt-2">
          <Button type="button" variant="ghost" size="sm" className="rounded-full" onClick={autoDetect} data-testid="field-map-auto-button"><Wand2 size={14} className="mr-1" /> Auto-detect</Button>
          <div className="flex gap-2">
            <Button type="button" variant="outline" className="rounded-full" onClick={onClose}>Cancel</Button>
            <Button type="button" data-testid="field-map-save-button" className="rounded-full bg-emerald-900 hover:bg-emerald-800 dark:bg-emerald-500 dark:text-slate-950" disabled={saving} onClick={save}>{saving ? "Saving…" : "Save mapping"}</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
